import axios from "axios";
import Cookies from "js-cookie";
import Help from "../utils/helper";
import { themeColors } from "@/theme/colors";
import { formatBillQueue } from "../utils/billQueue";
import { confirmDialogBus } from "@/utils/confirmDialog";
const themes = localStorage.getItem("darkMode");
const themClass = themes ? "theme-dark" : "theme-light";
const themClassPay = themes ? "theme-dark payment" : "theme-light payment";

/**
 * Utility function to format money with fallback
 */
const formatMoney = (amount) => {
  if (Help && Help.moneyFormatPrint) {
    return Help.moneyFormatPrint(amount);
  }
  // Fallback formatting
  if (amount === null || amount === undefined) return "0.00";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return num
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Ensure the application regains focus so hardware scanners (keyboard emulation)
 * continue to deliver key events to the app immediately.
 */
const restoreAppFocus = () => {
  try {
    setTimeout(() => {
      try {
        if (document && document.body && document.body.focus) {
          document.body.focus();
        }
        if (window && window.focus) {
          window.focus();
        }
      } catch (_) {
        // ignore
      }
    }, 0);
  } catch (_) {
    // ignore
  }
};

const cleanupStaleSwalContainers = () => {
  document.querySelectorAll(".swal2-container").forEach((el) => el.remove());
};

const onPaymentAlertBeforeOpen = () => {
  const scanner = document.querySelector(".scanner-capture");
  if (scanner && typeof scanner.blur === "function") {
    scanner.blur();
  }
};

const onPaymentAlertDidClose = () => {
  cleanupStaleSwalContainers();
  restoreAppFocus();
};

/** After button swap: confirm = Success, cancel = Print */
const shouldPrintFromPaymentAlert = (result) => {
  return Boolean(
    result && result.isDismissed && result.dismiss === "cancel"
  );
};

/** Laravel validation shape: `{ field: ["msg"] }` → single readable string */
const flattenValidationMessages = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return "";
  }
  const parts = [];
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item != null && item !== "") {
          parts.push(String(item));
        }
      });
    } else if (v != null && v !== "") {
      parts.push(String(v));
    }
  });
  return parts.join(" ").trim();
};

/**
 * Only pass known locale keys through `$t`. Raw API / exception prose is shown as-is so
 * vue-i18n linked-message / parsing quirks cannot corrupt user-visible text.
 */
const resolveAlertMessage = (props, raw, fallbackKey) => {
  const fb =
    fallbackKey != null && fallbackKey !== "" ? props.$t(fallbackKey) : "";
  if (raw === null || raw === undefined || raw === "") {
    return fb;
  }
  if (typeof raw === "object") {
    const flat = flattenValidationMessages(raw);
    return flat || fb;
  }
  const s = String(raw).trim();
  if (!s) {
    return fb;
  }
  try {
    if (props.$te(s)) {
      return props.$t(s);
    }
  } catch (_) {
    // ignore — show raw string
  }
  return s;
};

const requestConfirm = (dialog) => {
  return new Promise((resolve) => {
    confirmDialogBus.$emit("open", { dialog, resolve });
  });
};

const confirmAlert = async (props, title) => {
  return requestConfirm({
    title: props.$t("confirm"),
    text: title,
    cancelText: props.$t("cancel"),
    confirmText: props.$t("ok"),
    confirmColor: themeColors.success,
  });
};
/**
 *
 *
 * @param {*} props
 * @param {*} title
 */
const success = async (props, text) => {
  const _title = props.$t("success");
  await props.$swal.fire({
    icon: "success",
    title: _title,
    text: resolveAlertMessage(props, text, "process_success"),
    showConfirmButton: false,
    timer: 1500,
  });
};

const successSale = async (props, price, title) => {
  const _print = props.$t("print");
  const _success = props.$t("success");
  const result = await props.$swal.fire({
    icon: "success",
    title: price,
    text: title,
    showConfirmButton: true,
    showCancelButton: true,
    cancelButtonColor: themeColors.cancel,
    confirmButtonColor: themeColors.primary,
    confirmButtonText: _print,
    cancelButtonText: _success,
    customClass: themClassPay,
  });
  return result;
};

const successPos = async (props, data, showAction = true) => {
  try {
    // Extract all necessary data with defaults
    const {
      total_price = 0,
      status = "pending",
      total_paid = 0,
      total_discount = 0,
      type = "cash",
      code = "",
      customer_id = null,
      store_id = null,
      branch_id = null,
      history = [],
      created_at = null,
      queue_no = null,
    } = data;

    // Get translations
    const _cash_back = props.$t("cash_back");
    const _not_enough = props.$t("not_enough");
    const _full_payment = props.$t("full_payment");
    const _overdue = props.$t("overdue");
    const _price = props.$t("price");
    const _print = props.$t("print");
    const _success = props.$t("success");
    const _discount = props.$t("discount");
    const _payment_method = props.$t("payment_method");
    const _ticket = props.$t("ticket");
    const _customer = props.$t("customer");
    const _store = props.$t("store");
    const _branch = props.$t("branch");
    const _items = props.$t("items");
    const _total_items = props.$t("total_items");
    const _queue_number = props.$t("queue_number");

    // Calculate payment details
    const actualAmount = total_price - total_discount;
    const remainingAmount = actualAmount - total_paid;
    const cashBack = total_paid > actualAmount ? total_paid - actualAmount : 0;

    // Format amounts
    const formattedTotalPrice = formatMoney(total_price);
    const formattedActualAmount = formatMoney(actualAmount);
    const formattedTotalPaid = formatMoney(total_paid);
    const formattedCashBack = formatMoney(cashBack);
    const formattedDiscount = formatMoney(total_discount);
    const formattedRemaining = formatMoney(remainingAmount);

    // Determine message and icon based on payment status
    let message = "";
    let icon = "success";
    let title = "";
    let details = [];

    if (status === "done") {
      if (total_paid >= actualAmount) {
        // Payment completed successfully
        title = _full_payment;
        icon = "success";

        if (cashBack > 0) {
          message = `${_cash_back} ${formattedCashBack}`;
          details.push(`${_price}: ${formattedTotalPaid}`);
          details.push(`${_cash_back}: ${formattedCashBack}`);
        } else {
          message = _full_payment;
          details.push(`${_price}: ${formattedTotalPaid}`);
        }

        if (total_discount > 0) {
          details.push(`${_discount}: ${formattedDiscount}`);
        }
      } else {
        // Partial payment
        title = _not_enough;
        icon = "warning";
        message = `${_not_enough} ${formattedRemaining}`;
        details.push(`${_price}: ${formattedTotalPrice}`);
        details.push(`${_discount}: ${formattedDiscount}`);
        details.push(`${_not_enough}: ${formattedRemaining}`);
      }
    } else if (status === "overdue") {
      title = _overdue;
      icon = "error";
      message = _overdue;
      details.push(`${_price}: -${formattedTotalPrice}`);
    } else if (status === "WAITING_PAYMENT") {
      title = "Payment Pending";
      icon = "info";
      message = "Payment is pending";
      details.push(`${_price}: ${formattedTotalPrice}`);
    } else {
      title = "Payment Status";
      icon = "info";
      message = `Status: ${status}`;
      details.push(`${_price}: ${formattedTotalPrice}`);
    }

    // Add additional sale information
    if (code) {
      details.push(`${_ticket}: ${code}`);
    }

    const queueLabel = formatBillQueue(queue_no);
    if (queueLabel) {
      details.push(`${_queue_number}: ${queueLabel}`);
    }

    if (type && type !== "cash") {
      details.push(`${_payment_method}: ${type.toUpperCase()}`);
    }

    if (history && history.length > 0) {
      details.push(`${_total_items}: ${history.length}`);
    }

    // Create enhanced alert
    const result = await props.$swal.fire({
      icon: icon,
      title: title,
      html: `
      <div class="sale-success-alert">              
        <div class="sale-details" style="text-align: left; margin: 20px 0; padding: 15px; background: #f5f5f5; border-radius: 8px;">
          ${details
            .map(
              (detail) =>
                `<div style="margin: 8px 0; font-size: 14px;">${detail}</div>`
            )
            .join("")}
        </div>      
      </div>
    `,
      showConfirmButton: showAction,
      showCancelButton: showAction,
      cancelButtonColor: themeColors.cancel,
      confirmButtonColor: themeColors.primary,
      confirmButtonText: _success,
      cancelButtonText: _print,
      customClass: themClassPay,
      width: "500px",
      onBeforeOpen: onPaymentAlertBeforeOpen,
      onAfterClose: onPaymentAlertDidClose,
    });

    return result;
  } catch (error) {
    console.error("Error in successPos:", error);
    // Fallback to simple success message
    return await props.$swal.fire({
      icon: "success",
      title: "Success",
      text: "Operation completed successfully",
      showConfirmButton: true,
      confirmButtonColor: themeColors.primary,
    });
  }
};

/**
 *
 *
 * @param {*} props
 * @param {*} title
 */
const error = async (props, item) => {
  let error = [];
  const _title = props.$t("error");
  const _msg = props.$t(item);
  if (item !== null && item !== undefined) {
    if (typeof item === "object") {
      // iterate over object
      Object.keys(item).forEach((key, index) => {
        console.log(`${key}: ${item[key]}`);
        error = item[key];
      });
    } else {
      error = _msg;
    }
    await props.$swal.fire({
      icon: "error",
      title: _title,
      text: error,
      showConfirmButton: true,
      confirmButtonColor: themeColors.primary,
    });
  }
};

/**
 *
 *
 * @param {*} props
 * @param {*} title
 */
const cancel = async (props, title) => {
  const _success = props.$t("success");
  await props.$swal.fire({
    icon: "error",
    title: _success,
    text: title,
    showConfirmButton: false,
    timer: 1500,
  });
};

const handleError = async (prop, errorType) => {
  switch (errorType.code) {
    case 400 || 409 || 404:
      await error(prop, errorType.message);
      break;
    case 401:
      await axios.post("/api/app/logout");
      const _mgs = prop.$t("session_expired");
      await prop.$swal.fire({
        icon: "error",
        title: _mgs,
        text: errorType.message,
        showConfirmButton: true,
      });
      Cookies.remove("token");
      Cookies.remove("user");
      Cookies.remove("isLoggedIn");
      delete axios.defaults.headers.common["Authorization"];
      window.location.href = "/login";
      break;
    default:
      await error(prop, errorType.message);
      break;
  }
};
const toast = async (props, title, type) => {
  // pages' own error toasts are noise once the branch is known to be expired (/manage-branches explains it)
  if (type === "error" && require("@/plugins/axios").isBranchExpiredSeen()) return;
  const msg = resolveAlertMessage(props, title, "new_notification");
  const _msg1 = props.$t("new_notification");
  await props.$toast.open({
    type: type ? type : "success",
    message: msg || _msg1,
  });
};

const errorAlert = async (self, item) => {
  let error = [];
  if (
    item !== null &&
    item !== undefined &&
    item.message !== null &&
    item.message !== undefined
  ) {
    if (typeof item.message === "object") {
      Object.keys(item.message).forEach((key, index) => {
        console.log(`${key}: ${item.message[key]}`);
        error = item.message[key];
      });
    } else {
      error = item.message;
    }
    const _title = self.$t("failed");
    const _ok = self.$t("ok");
    const result = await self.$swal.fire({
      icon: "error",
      title: _title,
      text: error,
      showConfirmButton: true,
      confirmButtonColor: themeColors.primary,
      confirmButtonText: _ok,
      customClass: themClass,
    });
    return result;
  }
};
const confirm = async ({
  props,
  title = "are_you_sure",
  text = "want_to_proceed",
  textParams = null,
  con_txt = "ok",
  can_txt = "cancel",
  color_con = themeColors.primary,
  color_can = themeColors.cancel,
  showCancel = true,
}) => {
  const _title = props.$t(title);
  // If textParams is provided, use it for translation with parameters
  // Otherwise, check if text is already translated (contains non-ASCII characters or is a long string)
  let _text;
  if (textParams) {
    _text = props.$t(text, textParams);
  } else if (
    typeof text === "string" &&
    (text.length > 50 || /[^\x00-\x7F]/.test(text))
  ) {
    // Already translated string (contains non-ASCII characters or is long)
    _text = text;
  } else {
    _text = props.$t(text);
  }

  // Check if con_txt and can_txt are already translated
  const _con_txt =
    typeof con_txt === "string" &&
    (con_txt.length > 10 || /[^\x00-\x7F]/.test(con_txt))
      ? con_txt
      : props.$t(con_txt);
  const _can_txt =
    typeof can_txt === "string" &&
    (can_txt.length > 10 || /[^\x00-\x7F]/.test(can_txt))
      ? can_txt
      : props.$t(can_txt);

  const result = await requestConfirm({
    title: _title,
    text: _text,
    cancelText: _can_txt,
    confirmText: _con_txt,
    confirmColor: color_con,
    showCancel,
  });

  return result;
};

/**
 * Enhanced success alert for complete sales with detailed information
 */
const successCompleteSale = async (props, data) => {
  try {
    // Extract sale data
    const {
      code = "",
      total_price = 0,
      total_price_sale = 0,
      total_discount = 0,
      tax_amount = 0,
      total_paid = 0,
      type = "cash",
      customer_id = null,
      store_id = null,
      branch_id = null,
      history = [],
      created_at = null,
      note = "",
      currency_id = null,
      exchange_rate = 1,
      payment_currency = null,
      total_paid_lak = null,
      change = 0,
      queue_no = null,
    } = data;

    // Get translations
    const _sale_completed =
      props.$t("sale_completed") || "Sale completed successfully";
    const _ticket_number = props.$t("ticket_number") || "ເລກບິນ";
    const _sub_total = props.$t("sub_total") || "Sub total";
    const _discount_applied =
      props.$t("discount_applied") || "Discount Applied";
    const _tax = props.$t("tax") || "Tax";
    const _amount_paid = props.$t("amount_paid") || "Amount Paid";
    const _cash_back = props.$t("cash_back") || "Cash Back";
    const _payment_method = props.$t("payment_method") || "Payment Method";
    const _items_sold = props.$t("items_sold") || "Items sold";
    const _customer_info = props.$t("customer") || "Customer";
    const _store_info = props.$t("store_info") || "Store";
    const _branch_info = props.$t("branch_info") || "Branch";
    const _date_time = props.$t("date_time") || "Date & Time";
    const _print = props.$t("print") || "Print";
    const _success = props.$t("success");
    const _note = props.$t("note") || "Note";
    const _cross_currency_payment =
      props.$t("cross_currency_payment") || "Cross-Currency Payment";
    const _exchange_rate = props.$t("exchange_rate") || "Exchange Rate";
    const _payment_currency =
      props.$t("payment_currency") || "Payment Currency";
    const _grand_total = props.$t("grand_total") || "Total Payment";
    const _queue_number = props.$t("queue_number");
    const queueLabel = formatBillQueue(queue_no);
    const payment_method_name = getPaymentTypeDisplay(type, props);

    // Determine currencies from backend data
    // Get actual product currency from cart history
    let productCurrency = "LAK"; // Default to LAK
    if (history && history.length > 0) {
      const firstItem = history[0];
      if (firstItem.product && firstItem.product.currency) {
        productCurrency = firstItem.product.currency.code || "LAK";
      }
    }

    const paymentCurrency =
      payment_currency?.code || currency_id?.code || "LAK"; // Payment currency
    const isCrossCurrency = productCurrency !== paymentCurrency;

    // Calculate original subtotal before discount
    // Use total_price_sale if available, otherwise calculate: total_price + total_discount
    const originalSubtotal = total_price_sale > 0 
      ? total_price_sale 
      : total_price + total_discount;

    // Format amounts
    const formattedSubtotal =
      props.$options.filters.NumberFormat(originalSubtotal);
    const formattedTotalPrice =
      props.$options.filters.NumberFormat(total_price);
    const formattedDiscount =
      props.$options.filters.NumberFormat(total_discount);
    const formattedTax =
      props.$options.filters.NumberFormat(tax_amount);
    // Calculate actual amount paid: if change exists, customer paid more than the bill
    // total_paid from API is what customer actually paid, change is what we give back
    // So actualAmountPaid = total_paid (what customer gave us)
    const actualAmountPaid = total_paid;
    const formattedPaid = props.$options.filters.NumberFormat(actualAmountPaid);
    const formattedChange = props.$options.filters.NumberFormat(change);

    // Format date
    const saleDate = created_at
      ? new Date(created_at).toLocaleString()
      : new Date().toLocaleString();

    // Get customer name if available
    const customerName =
      customer_id && typeof customer_id === "object"
        ? customer_id.name || customer_id.first_name || "N/A"
        : "N/A";

    // Get store name if available
    const storeName =
      store_id && typeof store_id === "object" ? store_id.name || "N/A" : "N/A";

    // Get tax rate from auth store
    const taxRate = props.$store?.getters["auth/tax_rate"] ?? 10;

    // Get branch name if available
    const branchName =
      branch_id && typeof branch_id === "object"
        ? branch_id.name || "N/A"
        : "N/A";

    // Create detailed success alert
    const result = await props.$swal.fire({
      icon: "success",
      title: "",
      html: `
      <div class="complete-sale-alert" style="text-align: left;">
        <div class="sale-header" style="text-align: center; margin-bottom: 20px;">
          
          <h2 style="color: #00c853; margin: 0;">${_sale_completed}</h2>
          
        </div>

        <div class="sale-details" style="background: #f8f9fa; padding: 20px; border-radius: 10px; margin-bottom: 20px;">
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_ticket_number}</span>
            <span style="color: #495057;">${code}</span>
          </div>

          ${
            queueLabel
              ? `
          <div class="detail-row" style="display: flex; justify-content: space-between; align-items: center; margin: 8px 0; padding: 8px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_queue_number}</span>
            <span style="color: #438EF2; font-size: 24px; font-weight: 700;">${queueLabel}</span>
          </div>
          `
              : ""
          }
          
          ${
            total_discount > 0 || tax_amount > 0
              ? `
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_sub_total}</span>
            <span style="color: #495057;">${formattedSubtotal} ${productCurrency}</span>
          </div>
          `
              : ""
          }
          
          ${
            total_discount > 0
              ? `
            <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
              <span style="font-weight: 600;">${_discount_applied}</span>
              <span style="color: #dc3545;">-${formattedDiscount} ${productCurrency}</span>
            </div>
          `
              : ""
          }
          
          ${
            tax_amount > 0
              ? `
            <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
              <span style="font-weight: 600;">${_tax}${taxRate > 0 ? `(${taxRate}%)` : ""}</span>
              <span style="color: #495057;">+${formattedTax} ${productCurrency}</span>
            </div>
          `
              : ""
          }
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_amount_paid}</span>
            <div style="text-align: right;">
              <div style="color: #00c853; font-weight: 600;">${formattedPaid} ${paymentCurrency}</div>
            </div>
          </div>
          
          ${
            change > 0
              ? `
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_cash_back}</span>
            <span style="color: #f0b000; font-weight: 600;">${formattedChange} ${paymentCurrency}</span>
          </div>
          `
              : ""
          }
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_payment_method}</span>
            <span style="color: #495057; text-transform: uppercase;">${payment_method_name}</span>
          </div>
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_items_sold}</span>
            <span style="color: #495057;">${history.length}</span>
          </div>
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_date_time}</span>
            <span style="color: #495057;">${saleDate}</span>
          </div>
        </div>

        ${
          isCrossCurrency
            ? `
          <div class="cross-currency-info" style="background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%); padding: 15px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #2196f3;">
            <div style="text-align: center; margin-bottom: 10px;">
              <span style="color: #2196f3; font-weight: 600; font-size: 14px;">🌍 ${_cross_currency_payment}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin: 5px 0;">
              <span style="font-weight: 600;">${_exchange_rate}:</span>
              <span style="color: #495057;">1 ${productCurrency} = ${props.$options.filters.NumberFormat(
                exchange_rate
              )} ${paymentCurrency}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin: 5px 0;">
              <span style="font-weight: 600;">${_payment_currency}:</span>
              <span style="color: #00c853; font-weight: 600;">${paymentCurrency}</span>
            </div>
          </div>
        `
            : ""
        }         
            ${
              note
                ? `
              <div class="info-row" style="margin: 5px 0;">
                <span style="font-weight: 600;">${_note}:</span> ${note}
              </div>
            `
                : ""
            }
        
 
      </div>
    `,
      showConfirmButton: true,
      showCancelButton: true,
      cancelButtonColor: themeColors.cancel,
      confirmButtonColor: themeColors.primary,
      confirmButtonText: _success,
      cancelButtonText: _print,
      customClass: themClassPay,
      width: "600px",
      onBeforeOpen: onPaymentAlertBeforeOpen,
      onAfterClose: onPaymentAlertDidClose,
    });

    return result;
  } catch (error) {
    console.error("Error in successCompleteSale:", error);
    // Fallback to simple success message
    return await props.$swal.fire({
      icon: "success",
      title: "Sale Completed",
      text: "Sale completed successfully",
      showConfirmButton: true,
      confirmButtonColor: themeColors.primary,
    });
  }
};

/**
 * Enhanced success alert for restaurant checkout with table and seller information
 */
const successRestaurantCheckout = async (props, data) => {
  try {
    // Extract sale data
    const {
      code = "",
      total_price = 0,
      total_discount = 0,
      total_paid = 0,
      status = "done",
      type = "cash",
      customer_id = null,
      store_id = null,
      branch_id = null,
      table_id = null, // NEW: Table information
      user_id = null, // NEW: Seller information
      history = [],
      created_at = null,
      note = "",
      currency_id = null,
      exchange_rate = 1,
      total_price_sale = 0,
      payment_currency = null,
      total_paid_lak = null,
      payment_method = "cash",
      change = 0,
      queue_no = null,
    } = data;

    // Get translations
    const _sale_completed = props.$t("sale_completed") || "Sale Completed";
    const _ticket_number = props.$t("ticket_number") || "Ticket #";
    const _queue_number = props.$t("queue_number");
    const queueLabel = formatBillQueue(queue_no);
    const _table = props.$t("table") || "Table";
    const _seller = props.$t("seller") || "Seller";
    const _total_amount = props.$t("total_amount") || "Total Amount";
    const _discount_applied =
      props.$t("discount_applied") || "Discount Applied";
    const _amount_paid = props.$t("amount_paid") || "Amount Paid";
    const _cash_back = props.$t("cash_back") || "Cash Back";
    const _payment_method = props.$t("payment_method") || "Payment Method";
    const _items_sold = props.$t("items_sold") || "Items Sold";
    const _customer_info = props.$t("customer") || "Customer";
    const _store_info = props.$t("store_info") || "Store";
    const _branch_info = props.$t("branch_info") || "Branch";
    const _date_time = props.$t("date_time") || "Date & Time";
    const _print = props.$t("print");
    const _success = props.$t("success");
    const _note = props.$t("note") || "Note";
    const _cross_currency_payment =
      props.$t("cross_currency_payment") || "Cross-Currency Payment";
    const _exchange_rate = props.$t("exchange_rate") || "Exchange Rate";
    const _payment_currency =
      props.$t("payment_currency") || "Payment Currency";

    // Determine currencies from backend data
    const productCurrency = "LAK"; // Products are always priced in LAK
    const paymentCurrency =
      payment_currency?.code || currency_id?.code || "LAK"; // Payment currency
    const isCrossCurrency = productCurrency !== paymentCurrency;

    // Format amounts
    const formattedTotalPrice =
      props.$options.filters.NumberFormat(total_price);
    const formattedDiscount =
      props.$options.filters.NumberFormat(total_discount);
    const formattedPaid = props.$options.filters.NumberFormat(total_paid);
    const formattedChange = props.$options.filters.NumberFormat(change);

    // For cross-currency payments, also show LAK equivalent
    const formattedPaidLAK = total_paid_lak
      ? props.$options.filters.NumberFormat(total_paid_lak)
      : null;

    // Format date
    const saleDate = created_at
      ? new Date(created_at).toLocaleString()
      : new Date().toLocaleString();

    // Get table name if available
    const tableName =
      table_id && typeof table_id === "object" ? table_id.name || "N/A" : "N/A";

    // Get seller name if available
    const sellerName =
      user_id && typeof user_id === "object"
        ? user_id.first_name || "N/A"
        : "N/A";

    // Get customer name if available
    const customerName =
      customer_id && typeof customer_id === "object"
        ? customer_id.name || customer_id.first_name || "N/A"
        : "N/A";

    // Get store name if available
    const storeName =
      store_id && typeof store_id === "object" ? store_id.name || "N/A" : "N/A";

    // Get branch name if available
    const branchName =
      branch_id && typeof branch_id === "object"
        ? branch_id.name || "N/A"
        : "N/A";

    // Create detailed success alert
    const result = await props.$swal.fire({
      icon: "success",
      title: "",
      html: `
      <div class="complete-sale-alert" style="text-align: left;">
        <div class="sale-header" style="text-align: center; margin-bottom: 20px;">
          
          <h2 style="color: #00c853; margin: 0;">${_sale_completed}</h2>
          
        </div>

        <div class="sale-details" style="background: #f8f9fa; padding: 20px; border-radius: 10px; margin-bottom: 20px;">
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_ticket_number}</span>
            <span style="color: #495057;">${code}</span>
          </div>

          ${
            queueLabel
              ? `
          <div class="detail-row" style="display: flex; justify-content: space-between; align-items: center; margin: 8px 0; padding: 8px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_queue_number}</span>
            <span style="color: #438EF2; font-size: 24px; font-weight: 700;">${queueLabel}</span>
          </div>
          `
              : ""
          }
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_table}</span>
            <span style="color: #495057;">${tableName}</span>
          </div>
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_total_amount}</span>
            <span style="color: #495057;">${formattedTotalPrice} ${productCurrency}</span>
          </div>
          
          ${
            total_discount > 0
              ? `
            <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
              <span style="font-weight: 600;">${_discount_applied}</span>
              <span style="color: #dc3545;">-${formattedDiscount} ${productCurrency}</span>
            </div>
          `
              : ""
          }
          
                     <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
             <span style="font-weight: 600;">${_amount_paid}</span>
            <div style="text-align: right;">
              <div style="color: #00c853; font-weight: 600;">${formattedPaid} ${paymentCurrency}</div>
            </div>
           </div>
           
           ${
             change > 0
               ? `
           <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
             <span style="font-weight: 600;">${_cash_back}</span>
            <span style="color: #f0b000; font-weight: 600;">${formattedChange} ${paymentCurrency}</span>
           </div>
           `
               : ""
           }
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_payment_method}</span>
            <span style="color: #495057; text-transform: uppercase;">${type}</span>
          </div>
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_items_sold}</span>
            <span style="color: #495057;">${history.length}</span>
          </div>
          
          <div class="detail-row" style="display: flex; justify-content: space-between; margin: 8px 0; padding: 5px 0; border-bottom: 1px solid #e9ecef;">
            <span style="font-weight: 600;">${_date_time}</span>
            <span style="color: #495057;">${saleDate}</span>
          </div>
        </div>

        ${
          isCrossCurrency
            ? `
          <div class="cross-currency-info" style="background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%); padding: 15px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #2196f3;">
            <div style="text-align: center; margin-bottom: 10px;">
              <span style="color: #2196f3; font-weight: 600; font-size: 14px;">🌍 ${_cross_currency_payment}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin: 5px 0;">
              <span style="font-weight: 600;">${_exchange_rate}:</span>
              <span style="color: #495057;">1 ${productCurrency} = ${props.$options.filters.NumberFormat(
                exchange_rate
              )} ${paymentCurrency}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin: 5px 0;">
              <span style="font-weight: 600;">${_payment_currency}:</span>
              <span style="color: #00c853; font-weight: 600;">${paymentCurrency}</span>
            </div>
          </div>
        `
            : ""
        }

        ${
          storeName !== "N/A" ||
          branchName !== "N/A" ||
          sellerName !== "N/A" ||
          customerName !== "N/A"
            ? `
          <div class="additional-info" style="background: #e3f2fd; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
            
            ${
              storeName !== "N/A"
                ? `
              <div class="info-row" style="margin: 5px 0;">
                <span style="font-weight: 600;">${_store_info}:</span> ${storeName}
              </div>
            `
                : ""
            }
            ${
              branchName !== "N/A"
                ? `
              <div class="info-row" style="margin: 5px 0;">
                <span style="font-weight: 600;">${_branch_info}:</span> ${branchName}
              </div>
            `
                : ""
            }
            ${
              sellerName !== "N/A"
                ? `
              <div class="info-row" style="margin: 5px 0;">
                <span style="font-weight: 600;">${_seller}:</span> ${sellerName}
              </div>
            `
                : ""
            }
                ${
                  customerName !== "N/A"
                    ? `
              <div class="info-row" style="margin: 5px 0;">
                <span style="font-weight: 600;">${_customer_info}:</span> ${customerName}
              </div>
            `
                    : ""
                }
                
          </div>
        `
            : ""
        }
            ${
              note
                ? `
              <div class="info-row" style="margin: 5px 0;">
                <span style="font-weight: 600;">${_note}:</span> ${note}
              </div>
            `
                : ""
            }
        
 
      </div>
    `,
      showConfirmButton: true,
      showCancelButton: true,
      cancelButtonColor: themeColors.cancel,
      confirmButtonColor: themeColors.primary,
      confirmButtonText: _success,
      cancelButtonText: _print,
      customClass: themClassPay,
      width: "600px",
      allowOutsideClick: false, // Prevent closing when clicking outside
      onBeforeOpen: onPaymentAlertBeforeOpen,
      onAfterClose: onPaymentAlertDidClose,
    });

    return result;
  } catch (error) {
    console.error("Error in successRestaurantCheckout:", error);
    // Fallback to simple success message
    return await props.$swal.fire({
      icon: "success",
      title: "Sale Completed",
      text: "Sale completed successfully",
      showConfirmButton: true,
      confirmButtonColor: themeColors.primary,
    });
  }
};

/**
 * Utility function to get payment type display name
 */
const getPaymentTypeDisplay = (type, props) => {
  const paymentTypes = {
    qr: props.$t("qr_code") || "QR",
    cash: props.$t("cash") || "Cash",
    transfer: props.$t("transfer") || "Transfer",
    credit: props.$t("credit") || "Credit",
    cod: props.$t("cod") || "COD",
    cash_transfer: props.$t("cash_with_transfer") || "Cash Transfer",
  };

  return paymentTypes[type] || type.toUpperCase();
};

/**
 * Utility function to get status color and icon
 */
const getStatusDisplay = (status, total_paid, total_price) => {
  if (status === "done") {
    if (total_paid >= total_price) {
      return { color: "#00c853", icon: "🎉", text: "Payment Complete" };
    } else {
      return { color: "#ff9800", icon: "⚠️", text: "Partial Payment" };
    }
  } else if (status === "overdue") {
    return { color: "#f44336", icon: "⏰", text: "Payment Overdue" };
  } else if (status === "WAITING_PAYMENT") {
    return { color: "#2196f3", icon: "⏳", text: "Waiting for Payment" };
  } else {
    return { color: "#9e9e9e", icon: "ℹ️", text: `Status: ${status}` };
  }
};

export {
  toast,
  confirmAlert,
  success,
  error,
  cancel,
  handleError,
  successPos,
  successSale,
  successCompleteSale,
  successRestaurantCheckout,
  errorAlert,
  confirm,
  restoreAppFocus,
  shouldPrintFromPaymentAlert,
};
